/**
 * `datePublished`/`dateModified` for a knowledge-base blog article's JSON-LD, and for anything
 * else that needs the same two dates.
 *
 * The naive version — `updatedAt || createdAt` — is Mongoose's write timestamp on
 * `blog_articles`, not the article's editorial history: 1,036 of 1,197 KB articles share one
 * `updatedAt` (2026-09-14T10:46:00.773Z) because a bulk write touched them all the same second.
 * The date that actually means something is the frontmatter `last_updated:` field the knowledge
 * base writes as `lastUpdated` (a "YYYY-MM-DD" string, `tools/build_article_documents.py`), and
 * the page's own "Updated <date>" line already reads it
 * (`frontend/src/components/website/pages/DiscoverArticle.tsx`) — so leaving JSON-LD on
 * `updatedAt` means the schema and the visible date disagree on today's page.
 *
 * `lastUpdated` can't just replace `dateModified` outright, though: `publishedAt` is stamped at
 * first sync (2026-09-14 for the ~750 migrated legacy articles), and a good number of those
 * carry a `lastUpdated` from BEFORE that — 2026-05-11/12 for the ones checked. Emitting
 * `dateModified: lastUpdated` unchanged would put the modified date before the published date,
 * which is its own kind of wrong (and something crawlers flag). So `datePublished` has to move
 * to match: whichever of `publishedAt`/`createdAt` and `lastUpdated` is EARLIER becomes
 * `datePublished`, keeping its own original string rather than a reformatted one, which
 * guarantees `datePublished <= dateModified` by construction instead of by coincidence.
 *
 * Scoped to knowledge-base articles. The legacy Strapi shape this same route also renders has no
 * `lastUpdated` field and keeps its existing `publishedAt || createdAt` / `updatedAt || datePublished`
 * behaviour untouched — see the `isKb` branch in `marketingRoutes.tsx`'s `blogArticleHead`.
 */

export interface KbArticleDateInputs {
	/** Frontmatter `last_updated:`, "YYYY-MM-DD". Absent on articles the KB hasn't touched since sync. */
	lastUpdated?: string | null;
	publishedAt?: string | null;
	createdAt?: string | null;
	updatedAt?: string | null;
}

export interface KbArticleDates {
	datePublished?: string;
	dateModified?: string;
}

/** `NaN` on an unparsable string, never a thrown error — a bad date must lose the comparison, not the page. */
const timeOf = (value: string | null | undefined): number => (value ? new Date(value).getTime() : NaN);

export function resolveKbArticleDates(input: KbArticleDateInputs): KbArticleDates {
	const publishedRaw = input.publishedAt || input.createdAt || undefined;

	// `last_updated:` is hand-typed frontmatter and nothing validates it, so a typo would otherwise
	// reach the JSON-LD verbatim. An unparsable value counts as absent.
	const lastUpdated = input.lastUpdated && !Number.isNaN(timeOf(input.lastUpdated)) ? input.lastUpdated : undefined;

	// The earlier of (publishedAt || createdAt) and lastUpdated, comparing as dates but emitting
	// the ORIGINAL string of whichever wins — never a value reformatted through `Date`, since
	// `publishedAt` carries time-of-day precision `lastUpdated` does not, and swapping in a
	// recomputed ISO string would apply that precision to a date that never had it.
	let datePublished = publishedRaw;
	if (lastUpdated) {
		const publishedTime = timeOf(publishedRaw);
		if (!publishedRaw || Number.isNaN(publishedTime) || timeOf(lastUpdated) < publishedTime) {
			datePublished = lastUpdated;
		}
	}

	// `lastUpdated` first because it's the only one of the three an editor's actual edit moves;
	// `updatedAt` next because it is at least true of the DOCUMENT even where it isn't true of the
	// prose; `datePublished` last so a modified date never renders as "less known" than a
	// published one when neither of the first two exists.
	const dateModified = lastUpdated || input.updatedAt || datePublished;

	return { datePublished, dateModified };
}
